import { create } from 'zustand'
import axios from 'axios'
import { getApiBasePath } from '../config/apiBase'

interface User {
  id: string
  username: string
  email: string
  tenant_id: string
  role?: string
}

function normalizeUser(data: Record<string, unknown>): User {
  return {
    id: String(data.id ?? data.Id ?? ''),
    username: String(data.username ?? data.Username ?? ''),
    email: String(data.email ?? data.Email ?? ''),
    tenant_id: String(data.tenant_id ?? data.TenantId ?? ''),
    role: data.role ? String(data.role) : undefined,
  }
}

function loadUserFromStorage(): User | null {
  try {
    const raw = localStorage.getItem('user')
    if (!raw) return null
    const data = JSON.parse(raw) as Record<string, unknown>
    const user = normalizeUser(data)
    return user.id ? user : null
  } catch {
    return null
  }
}

async function fetchAndStoreUser(token: string): Promise<User> {
  const API_BASE = getApiBasePath()
  const userResponse = await axios.get(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  const user = normalizeUser(userResponse.data as Record<string, unknown>)
  localStorage.setItem('user', JSON.stringify(user))
  return user
}

interface AuthState {
  user: User | null
  token: string | null
  login: (username: string, password: string) => Promise<void>
  register: (fullName: string, email: string, password: string) => Promise<void>
  loginWithGoogle: (token: string, tokenType?: string) => Promise<void>
  logout: () => void
}

export const useAuthStore = create<AuthState>((set) => ({
  user: loadUserFromStorage(),
  token: localStorage.getItem('token'),

  // ── local login ──────────────────────────────────────────────────────────
  login: async (username: string, password: string) => {
    const API_BASE = getApiBasePath()
    try {
      const response = await axios.post(`${API_BASE}/auth/login`, {
        username,
        password,
        auth_provider: 'local',
      })
      const { access_token } = response.data
      if (!access_token) throw new Error('No token in response')
      localStorage.setItem('token', access_token)
      const user = await fetchAndStoreUser(access_token)
      set({ user, token: access_token })
    } catch (error: any) {
      const msg =
        error.response?.data?.detail ||
        (Array.isArray(error.response?.data?.detail) ? error.response?.data?.detail[0]?.msg : null) ||
        error.message ||
        'Login failed'
      throw new Error(typeof msg === 'string' ? msg : 'Login failed')
    }
  },

  // ── self-registration ────────────────────────────────────────────────────
  register: async (fullName: string, email: string, password: string) => {
    const API_BASE = getApiBasePath()
    try {
      const response = await axios.post(`${API_BASE}/auth/register`, {
        full_name: fullName,
        email,
        password,
      })
      const { access_token } = response.data
      if (!access_token) throw new Error('No token in response')
      localStorage.setItem('token', access_token)
      const user = await fetchAndStoreUser(access_token)
      set({ user, token: access_token })
    } catch (error: any) {
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Registration failed'
      throw new Error(typeof msg === 'string' ? msg : 'Registration failed')
    }
  },

  // ── Google OAuth ─────────────────────────────────────────────────────────
  loginWithGoogle: async (token: string, tokenType: string = 'access_token') => {
    const API_BASE = getApiBasePath()
    try {
      // Exchange Google access_token for user info, then send email + sub to backend
      let credential = token

      if (tokenType === 'access_token') {
        // Fetch user info from Google using the access token
        const userInfoRes = await axios.get('https://www.googleapis.com/oauth2/v3/userinfo', {
          headers: { Authorization: `Bearer ${token}` },
        })
        const googleUser = userInfoRes.data
        // Send the user's info directly to a backend endpoint that creates/finds the user
        const response = await axios.post(`${API_BASE}/auth/google-userinfo`, {
          sub: googleUser.sub,
          email: googleUser.email,
          given_name: googleUser.given_name,
          family_name: googleUser.family_name,
          email_verified: googleUser.email_verified,
          picture: googleUser.picture,
        })
        const { access_token: jwt } = response.data
        if (!jwt) throw new Error('No token in response')
        localStorage.setItem('token', jwt)
        const user = await fetchAndStoreUser(jwt)
        set({ user, token: jwt })
        return
      }

      // ID token flow (credential)
      const response = await axios.post(`${API_BASE}/auth/google-login`, { credential })
      const { access_token: jwt } = response.data
      if (!jwt) throw new Error('No token in response')
      localStorage.setItem('token', jwt)
      const user = await fetchAndStoreUser(jwt)
      set({ user, token: jwt })
    } catch (error: any) {
      const msg =
        error.response?.data?.detail ||
        error.message ||
        'Google sign-in failed'
      throw new Error(typeof msg === 'string' ? msg : 'Google sign-in failed')
    }
  },

  // ── logout ───────────────────────────────────────────────────────────────
  logout: () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    set({ user: null, token: null })
  },
}))
